import React from 'react';
import { Alert, Tag } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import type { ChatItem } from '../../hooks/useAgentChat';
import ToolStatusLine from './ToolStatusLine';
import ActionProposalCard from './ActionProposalCard';
import Markdown from './Markdown';
import CopyButton from './CopyButton';
import styles from './AgentMessageList.module.css';

interface AgentMessageListProps {
  items: ChatItem[];
  onConfirm: (itemId: string) => void;
  onAllowAlways?: (itemId: string) => void;
}

function renderItem(
  item: ChatItem,
  onConfirm: (itemId: string) => void,
  onAllowAlways?: (itemId: string) => void,
): React.ReactNode {
  switch (item.kind) {
    case 'user':
      return <div className={styles.user}>{item.text}</div>;
    case 'assistant':
      return (
        <div className={styles.assistantRow}>
          <div className={styles.assistant}>
            <Markdown text={item.text} />
          </div>
          {item.text && <CopyButton text={item.text} className={styles.bubbleCopy} />}
        </div>
      );
    case 'tool_status':
      return <ToolStatusLine label={item.label} />;
    case 'outcome':
      return (
        <div className={styles.outcome}>
          <Tag
            color={item.ok ? 'success' : 'error'}
            icon={item.ok ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          >
            {item.text}
          </Tag>
        </div>
      );
    case 'proposal':
      return (
        <div>
          <ActionProposalCard
            proposal={item.proposal}
            status={item.status}
            onConfirm={() => onConfirm(item.id)}
            onAllowAlways={onAllowAlways ? () => onAllowAlways(item.id) : undefined}
          />
          {item.proposal.suggested_route && (
            <div className={styles.deepLink}>
              <Link to={item.proposal.suggested_route}>Відкрити сторінку</Link>
            </div>
          )}
        </div>
      );
    case 'error':
      return (
        <Alert type="error" showIcon message={item.message} style={{ margin: '4px 0' }} />
      );
    default:
      return null;
  }
}

const AgentMessageList: React.FC<AgentMessageListProps> = ({ items, onConfirm, onAllowAlways }) => {
  return (
    <Virtuoso
      className={styles.virtuoso}
      data={items}
      followOutput="smooth"
      initialTopMostItemIndex={Math.max(0, items.length - 1)}
      computeItemKey={(_, item) => item.id}
      itemContent={(_, item) => (
        <div className={styles.row}>{renderItem(item, onConfirm, onAllowAlways)}</div>
      )}
    />
  );
};

export default AgentMessageList;
